import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { getTenantFromPath } from '../lib/tenant';
import { supabase } from '../lib/supabase';
import {
  LayoutDashboard,
  Users,
  BookOpen,
  Calendar,
  CheckCircle,
  CalendarCheck,
  CreditCard,
  Mail,
  Settings,
  LogOut,
  Menu,
  X,
  Link2,
  GraduationCap,
  UserPlus,
  Filter,
  Upload
} from 'lucide-react';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tenantLogo, setTenantLogo] = useState(null);
  const [tenantName, setTenantName] = useState('ClassroomDragon');
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { tenantId: urlTenantId } = useParams();

  // Get tenant from URL path (path-based routing)
  const pathTenantId = urlTenantId || getTenantFromPath();
  
  // Base path for navigation (includes tenant if in path-based mode)
  const basePath = pathTenantId ? `/${pathTenantId}` : '';

  // Detect if we're in the super-admin route context
  const isSuperAdminRoute = window.location.pathname.startsWith('/super-admin');
  const prefix = (user?.role === 'super_admin' && isSuperAdminRoute) ? '/super-admin' : '/dashboard';

  // Fetch tenant data including logo
  useEffect(() => {
    const fetchTenantData = async () => {
      // Determine tenant ID from user or path
      const effectiveTenantId = user?.tenant_id || user?.tenantId;
      
      if (!effectiveTenantId) {
        // Try to fetch by subdomain if we have a path tenant
        if (pathTenantId) {
          const { data: tenant } = await supabase
            .from('tenants')
            .select('id, logo_url, name')
            .eq('subdomain', pathTenantId)
            .single();
          
          if (tenant) {
            setTenantLogo(tenant.logo_url);
            setTenantName(tenant.name);
          }
        }
        return;
      }
      
      const { data: tenant } = await supabase
        .from('tenants')
        .select('logo_url, name')
        .eq('id', effectiveTenantId)
        .single();
      
      if (tenant) {
        setTenantLogo(tenant.logo_url);
        setTenantName(tenant.name);
      }
    };
    
    fetchTenantData();
  }, [user?.tenant_id, user?.tenantId, pathTenantId]);

  const handleLogout = () => {
    logout();
    // Redirect to appropriate login based on tenant context
    if (pathTenantId) {
      navigate(`/${pathTenantId}/login`);
    } else {
      navigate('/login');
    }
  };

  const getNavItems = () => {
    const baseItems = [];
    
    // Build paths with tenant prefix if in path-based mode
    const buildPath = (path) => `${basePath}${path}`;
    
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      const items = [
        { path: buildPath(prefix), icon: LayoutDashboard, label: 'Dashboard' },
        { path: buildPath(`${prefix}/students`), icon: Users, label: 'Students' },
        { path: buildPath(`${prefix}/classes`), icon: BookOpen, label: 'Classes' },
        { path: buildPath(`${prefix}/linked-classes`), icon: Link2, label: 'Linked Classes' },
        { path: buildPath(`${prefix}/enrollments`), icon: Calendar, label: 'Enrollments' },
        { path: buildPath(`${prefix}/attendance`), icon: CalendarCheck, label: 'Attendance' },
        { path: buildPath(`${prefix}/grading`), icon: GraduationCap, label: 'Grading' },
        { path: buildPath(`${prefix}/payments`), icon: CreditCard, label: 'Payments' },
        { path: buildPath(`${prefix}/communications`), icon: Mail, label: 'Communications' },
        { path: buildPath(`${prefix}/hiring`), icon: UserPlus, label: 'Hiring' },
        { path: buildPath(`${prefix}/crm/pipeline`), icon: Filter, label: 'CRM' },
        { path: buildPath(`${prefix}/settings`), icon: Settings, label: 'Settings' },
        { path: buildPath(`${prefix}/users/import`), icon: Upload, label: 'Import Parents' },
      ];
      
      return items;
    }
    
    if (user?.role === 'parent') {
      return [
        { path: buildPath('/parent'), icon: LayoutDashboard, label: 'Dashboard' },
        { path: buildPath('/parent/students'), icon: Users, label: 'My Children' },
        { path: buildPath('/parent/enrollments'), icon: Calendar, label: 'Enrollments' },
        { path: buildPath('/parent/payments'), icon: CreditCard, label: 'Payments' },
        { path: buildPath('/parent/communications'), icon: Mail, label: 'Messages' },
      ];
    }
    
    if (user?.role === 'employee') {
      return [
        { path: buildPath('/employee'), icon: LayoutDashboard, label: 'Dashboard' },
        { path: buildPath('/employee/classes'), icon: BookOpen, label: 'My Classes' },
        { path: buildPath('/employee/attendance'), icon: CheckCircle, label: 'Attendance' },
        { path: buildPath('/employee/communications'), icon: Mail, label: 'Messages' },
      ];
    }
    
    return baseItems;
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-purple-50">
      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-purple-100 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 flex items-center justify-center">
            {tenantLogo ? (
              <img src={tenantLogo || `${process.env.PUBLIC_URL}/favicon.svg`} alt={tenantName} className="w-full h-full object-contain" />
            ) : (
              <img src={`${process.env.PUBLIC_URL}/favicon.svg`} alt="ClassroomDragon" className="w-full h-full object-contain" />
            )}
          </div>
          <span className="font-bold text-purple-900 truncate max-w-[200px]">{tenantName}</span>
        </div>
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-purple-100"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-purple-100 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-purple-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-lg">
                {tenantLogo ? (
                  <img 
                    src={tenantLogo} 
                    alt={tenantName}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <img 
                    src={`${process.env.PUBLIC_URL}/favicon.svg`} 
                    alt="ClassroomDragon" 
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="font-bold text-purple-900 text-lg leading-tight">
                  Classroom<br/>Dragon
                </h1>
                <p className="text-xs text-purple-500">
                  {user?.role === 'super_admin' ? 'Super Admin Portal' : 
                   user?.role === 'admin' ? 'Admin Portal' : 
                   user?.role === 'parent' ? 'Parent Portal' : 
                   user?.role === 'employee' ? 'Employee Portal' : 'Portal'}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === `${basePath}${prefix}` || item.path === `${basePath}/parent` || item.path === `${basePath}/employee`}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                  }`
                }
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-purple-100">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-purple-200 rounded-full flex items-center justify-center">
                <span className="text-purple-700 font-semibold">
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-sm text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        <div className="p-6 lg:p-8">
          <Outlet />
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
